import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Check, Lock, ArrowRight, GraduationCap, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { ScoreGauge, scoreBandColor } from "../components/ScoreGauge";

function useDebounced(fn, delayMs) {
    const timer = useRef();
    const fnRef = useRef(fn);
    fnRef.current = fn;
    return useCallback(
        (...args) => {
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => fnRef.current(...args), delayMs);
        },
        [delayMs],
    );
}

export function TeacherReflectionsListPage() {
    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/teacher/cycles");
                setCycles(data);
            } catch (e) {
                setError(extractError(e, "Could not load cycles"));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <div className="text-[#0B2545] flex items-center gap-3"><Loader2 className="animate-spin" size={18} /> Loading…</div>;

    return (
        <div className="space-y-6" data-testid="teacher-reflections-list-page">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                    Instructional Core
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight">
                    My reflections
                </h1>
                <p className="mt-2 text-sm text-[#0E1B2C]/70 max-w-2xl">
                    Reflect on the Instructional Core look-fors. Your responses are private to you and help
                    inform your coach — they do not affect your rating.
                </p>
            </div>
            {error && <div className="text-red-700">{error}</div>}
            {cycles.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-10 text-center">
                    <ClipboardList size={28} className="mx-auto text-[#1E56A0]" />
                    <p className="mt-3 text-[#0B2545] font-semibold">No open cycles yet.</p>
                    <p className="text-sm text-[#0E1B2C]/70">A cycle needs to be started before you can reflect.</p>
                </div>
            ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {cycles.map((c) => (
                        <li key={c.id}>
                            <Link
                                to={`/app/teacher/reflections/${c.id}`}
                                className="block rounded-xl bg-white border border-[#D6E6F2] p-5 hover:border-[#8AC4FF] hover:shadow-lg hover:shadow-[#0B2545]/5 transition-all"
                                data-testid={`reflection-cycle-${c.id}`}
                            >
                                <div className="text-[11px] uppercase tracking-[0.25em] text-[#1E56A0] font-bold">
                                    {c.status.replace(/_/g, " ")}
                                </div>
                                <div className="mt-1 font-serif text-xl text-[#0B2545] font-semibold">{c.name}</div>
                                <div className="mt-3 inline-flex items-center gap-1 text-sm text-[#1E56A0]">
                                    Open reflection <ArrowRight size={14} />
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export function TeacherReflectionPage() {
    const { cycleId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState({}); // {lfId: boolean}
    const [savedAt, setSavedAt] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/teacher/reflections", { params: { cycle_id: cycleId } });
                setData(data);
                setError(null);
            } catch (e) {
                setError(extractError(e, "Could not load reflections"));
            } finally {
                setLoading(false);
            }
        })();
    }, [cycleId]);

    const debouncedSave = useDebounced(async (lfId, text) => {
        setSaving((s) => ({ ...s, [lfId]: true }));
        try {
            await api.put(`/teacher/reflections/${cycleId}/${lfId}`, { response_text: text });
            setSavedAt((s) => ({ ...s, [lfId]: new Date() }));
        } catch (e) {
            toast.error(extractError(e, "Could not save"));
        } finally {
            setSaving((s) => ({ ...s, [lfId]: false }));
        }
    }, 800);

    const setReflection = (indId, lfId, text) => {
        setData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                indicators: prev.indicators.map((ind) => (
                    ind.id !== indId ? ind : {
                        ...ind,
                        look_fors: ind.look_fors.map((lf) => (lf.id === lfId ? { ...lf, response_text: text } : lf)),
                    }
                )),
            };
        });
        debouncedSave(lfId, text);
    };

    if (loading) return <div className="text-[#0B2545] flex items-center gap-3"><Loader2 className="animate-spin" size={18} /> Loading…</div>;
    if (error || !data) return <div className="text-red-700">{error}</div>;

    return (
        <div className="space-y-6" data-testid="teacher-reflection-page">
            <Link to="/app/teacher/reflections" className="text-xs text-[#1E56A0] hover:underline">
                ← All reflections
            </Link>
            <div className="rounded-2xl overflow-hidden shadow-lg shadow-[#0B2545]/5">
                <div className="px-6 py-5 text-white" style={{ backgroundColor: "#1B3A7B" }}>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold opacity-90">
                        Instructional Core self-reflection
                    </div>
                    <h1 className="mt-1 font-serif text-3xl font-semibold">{data.cycle.name}</h1>
                    <p className="mt-3 text-sm text-white/85 max-w-3xl">
                        Your responses are private to you and help inform your coach. They do not affect your rating.
                    </p>
                </div>
            </div>
            {!data.writable && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 text-sm flex items-center gap-2">
                    <Lock size={14} /> This cycle is locked. Reflections are read-only.
                </div>
            )}
            <div className="space-y-6">
                {data.indicators.map((ind) => (
                    <div key={ind.id} className="rounded-2xl bg-white border border-[#D6E6F2] overflow-hidden" data-testid={`reflection-indicator-${ind.position}`}>
                        <div className="px-5 py-3 border-b border-[#D6E6F2]" style={{ background: "#1B3A7B0d" }}>
                            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1B3A7B]">
                                Indicator {ind.position}
                            </div>
                            <div className="font-serif text-lg text-[#0B2545] font-semibold">{ind.name}</div>
                        </div>
                        <div className="divide-y divide-[#D6E6F2]">
                            {ind.look_fors.map((lf) => (
                                <div key={lf.id} className="px-5 py-4" data-testid={`reflection-lookfor-${lf.id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-semibold text-[#0B2545]">{lf.position}. {lf.name}</div>
                                            <div className="mt-1 text-sm text-[#0E1B2C]/70 leading-relaxed">{lf.description}</div>
                                        </div>
                                        <div className="text-xs text-[#0E1B2C]/60 shrink-0">
                                            {saving[lf.id] ? <span className="text-amber-700 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" />Saving…</span>
                                                : savedAt[lf.id] ? <span className="text-emerald-700 inline-flex items-center gap-1"><Check size={12} />Saved</span>
                                                : null}
                                        </div>
                                    </div>
                                    <Textarea
                                        className="mt-3 min-h-[120px] bg-[#F8FBFF] border-[#D6E6F2]"
                                        placeholder="Reflect on how this shows up in your practice…"
                                        value={lf.response_text || ""}
                                        onChange={(e) => setReflection(ind.id, lf.id, e.target.value)}
                                        disabled={!data.writable}
                                        data-testid={`reflection-textarea-${lf.id}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function TeacherMyScoresPage() {
    const [cycles, setCycles] = useState([]);
    const [selectedCycleId, setSelectedCycleId] = useState(null);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/teacher/cycles");
                setCycles(data);
                if (data.length > 0) setSelectedCycleId(data[0].id);
                else setLoading(false);
            } catch (e) {
                setError(extractError(e, "Could not load cycles"));
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (!selectedCycleId) return;
        (async () => {
            setLoading(true);
            setMissing(false);
            try {
                const { data } = await api.get("/teacher/my-assessment", { params: { cycle_id: selectedCycleId } });
                setPayload(data);
                setError(null);
            } catch (e) {
                if (e?.response?.status === 404) {
                    setMissing(true);
                    setPayload(null);
                } else {
                    setError(extractError(e, "Could not load assessment"));
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedCycleId]);

    return (
        <div className="space-y-6" data-testid="teacher-my-scores-page">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                    Instructional Core
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight">
                    My scores
                </h1>
                <p className="mt-2 text-sm text-[#0E1B2C]/70 max-w-2xl">
                    Your Instructional Core scores from your coach — read-only.
                </p>
            </div>
            {cycles.length > 1 && (
                <div className="flex gap-2 flex-wrap" data-testid="my-scores-cycle-tabs">
                    {cycles.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setSelectedCycleId(c.id)}
                            className={`rounded-full px-3 py-1.5 text-sm border ${selectedCycleId === c.id ? "bg-[#0B2545] text-white border-[#0B2545]" : "bg-white text-[#0B2545] border-[#D6E6F2]"}`}
                            data-testid={`my-scores-cycle-${c.id}`}
                        >
                            {c.name}
                        </button>
                    ))}
                </div>
            )}
            {loading ? (
                <div className="text-[#0B2545] flex items-center gap-3"><Loader2 className="animate-spin" size={18} /> Loading…</div>
            ) : error ? (
                <div className="text-red-700">{error}</div>
            ) : missing || cycles.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-10 text-center" data-testid="my-scores-empty">
                    <GraduationCap size={28} className="mx-auto text-[#1E56A0]" />
                    <p className="mt-3 text-[#0B2545] font-semibold">No assessments yet.</p>
                    <p className="text-sm text-[#0E1B2C]/70">
                        Your instructional coach will let you know when your Instructional Core assessment is ready to review.
                    </p>
                </div>
            ) : payload ? (
                <>
                    <div className="rounded-2xl overflow-hidden shadow-lg shadow-[#0B2545]/5">
                        <div className="px-6 py-5 text-white flex flex-wrap justify-between items-center gap-4" style={{ backgroundColor: "#1B3A7B" }}>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.25em] font-bold opacity-90">
                                    {payload.cycle.name}
                                </div>
                                <h2 className="mt-1 font-serif text-2xl font-semibold">Instructional Core</h2>
                            </div>
                            <div className="bg-white/10 rounded-2xl p-2 border border-white/20">
                                <ScoreGauge value={payload.overall_rating} color="#FFFFFF" size={180} />
                            </div>
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5">
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                            Indicator scores
                        </div>
                        <div className="space-y-3">
                            {payload.indicators.map((ind) => (
                                <div key={ind.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-center" data-testid={`myscore-ind-${ind.position}`}>
                                    <div className="md:col-span-2 text-sm text-[#0B2545] font-semibold">
                                        {ind.position}. {ind.name}
                                    </div>
                                    <div className="md:col-span-3 h-3 rounded-full bg-[#E5EEF7] overflow-hidden">
                                        <div
                                            className="h-full transition-all"
                                            style={{
                                                width: ind.indicator_score != null ? `${(ind.indicator_score / 4) * 100}%` : 0,
                                                backgroundColor: scoreBandColor(ind.indicator_score),
                                            }}
                                        />
                                    </div>
                                    <div className="md:col-span-1 font-serif font-semibold text-[#0B2545]">
                                        {ind.indicator_score != null ? ind.indicator_score.toFixed(2) : "—"}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="myscore-heatmap">
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-3">
                            Look-for heatmap
                        </div>
                        <div className="space-y-2">
                            {payload.indicators.map((ind) => (
                                <div key={ind.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
                                    <div className="md:col-span-2 text-sm text-[#0B2545] font-semibold">
                                        {ind.position}. {ind.name}
                                    </div>
                                    <div className="md:col-span-4 grid grid-cols-4 gap-2">
                                        {ind.look_fors.map((lf) => (
                                            <div
                                                key={lf.id}
                                                className="rounded-md p-2 text-white text-xs font-semibold h-14 flex flex-col justify-between"
                                                style={{ backgroundColor: scoreBandColor(lf.rating) }}
                                                title={lf.name}
                                            >
                                                <div className="text-[10px] opacity-90 truncate">{ind.position}.{lf.position}</div>
                                                <div className="text-lg font-serif font-bold text-right">
                                                    {lf.rating != null ? lf.rating.toFixed(1) : "—"}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
}
